import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { SERVICES, CONTACT } from "@/data/site";

const linkCls = ({ isActive }) =>
  `relative text-sm font-medium transition-colors duration-300 hover:text-[#C8D484] ${isActive ? "text-[#C8D484]" : "text-white/80"}`;

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${
          scrolled || open ? "border-b border-white/10 bg-[#08090B]/80 backdrop-blur-xl" : "border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-[1440px] items-center justify-between px-5 sm:px-8 lg:px-12">
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3">
            <img src="/img/logo.jpg" alt="DOG'Z Training & Hotel" className="h-14 w-14 object-cover" />
            <span className="font-display text-2xl font-semibold uppercase leading-none tracking-tight">
              Dog<span className="text-[#C8D484]">'</span>z
              <span className="block font-mono text-[10px] font-normal tracking-[0.3em] text-white/70">Braga</span>
            </span>
          </Link>
          <nav className="hidden items-center gap-7 xl:flex" data-testid="desktop-nav">
            {SERVICES.map((s) => (
              <NavLink key={s.slug} to={`/servicos/${s.slug}`} className={linkCls} data-testid={`nav-${s.slug}`}>
                {s.name}
              </NavLink>
            ))}
            <NavLink to="/contacto" className={linkCls} data-testid="nav-contacto">
              Contactos
            </NavLink>
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/contacto"
              data-testid="header-cta-button"
              className="group hidden items-center gap-2 rounded-full bg-[#C8D484] px-5 py-2.5 text-sm font-bold text-black transition-transform duration-300 hover:scale-[1.04] sm:inline-flex"
            >
              Pedir orçamento
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </Link>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
              className="grid h-11 w-11 place-items-center rounded-full border border-white/20 xl:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>{open && <MobileMenu />}</AnimatePresence>
    </>
  );
};

const MobileMenu = () => (
  <motion.div
    data-testid="mobile-menu"
    initial={{ clipPath: "inset(0 0 100% 0)" }}
    animate={{ clipPath: "inset(0 0 0% 0)" }}
    exit={{ clipPath: "inset(0 0 100% 0)" }}
    transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
    className="fixed inset-0 z-40 flex flex-col justify-between bg-[#08090B] px-6 pb-10 pt-28"
  >
    <nav className="flex flex-col">
      {[...SERVICES.map((s) => ({ to: `/servicos/${s.slug}`, label: s.name, i: s.index, id: s.slug })), { to: "/contacto", label: "Contactos", i: "06", id: "contacto" }].map((l, k) => (
        <motion.div key={l.to} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 + k * 0.06 }}>
          <Link to={l.to} data-testid={`mobile-nav-${l.id}`} className="flex items-baseline gap-4 border-b border-white/10 py-4">
            <span className="font-mono text-xs text-[#C8D484]">{l.i}</span>
            <span className="font-display text-4xl font-semibold uppercase tracking-tight">{l.label}</span>
          </Link>
        </motion.div>
      ))}
    </nav>
    <div className="flex flex-col gap-2 text-sm text-white/70">
      <a href={CONTACT.phoneHref} className="text-lg font-semibold text-white">{CONTACT.phone}</a>
      <span>{CONTACT.address}</span>
    </div>
  </motion.div>
);
